import { Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import React, { useEffect, useState } from 'react';

import { FinanceType } from '../../../services/dtos/finances.dto';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { FileDto } from '../../../services/dtos/file.dto';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import NoData from '../../commun/no-data';
import { askFinanceFile, getFinancesFile } from '../../../services/finances.service';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { RelationType } from '../../../services/dtos/relation.dto';
/**
 * Ui pour la page d'assemblée générale
 * @constructor
 */
export default function FinanceFileUi(
    props: Readonly<{
        mandate?: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        type: FinanceType | RelationType;
        handlerSetType: (newFinance: FinanceType | RelationType | undefined, text: string | undefined) => void;
    }>
) {
    // Document du meeting à voir et télécharger
    const [file, setFile] = useState<FileDto>();
    // Document du meeting à voir et télécharger
    const [showFile, setShowFile] = useState<boolean>(false);
    // loader du meeting à voir et télécharger
    const [loading, setLoading] = useState(false);
    //SnackBar pour les messages
    const showMessage = useSnackbars();

    const loadFinanceFile = async (mandateId: string, type: FinanceType | RelationType) => {
        const result = await getFinancesFile(mandateId, type);
        if (!result) {
            setFile(undefined);
            return;
        }

        const pdf = await fetch(result.file);
        const content = await pdf.blob();
        setFile({ name: result.fileName, data: result.file, content });
    };

    /**
     * Selon l'onglet de la finance, on affiche directement le PDF ou non
     */
    useEffect(() => {
        if (!props.mandate) {
            return;
        }

        setShowFile(false);
        if (props.type === FinanceType.ADMISSION || props.type === FinanceType.BASHBOARD) {
            setShowFile(true);
            setLoading(true);
            loadFinanceFile(props.mandate.id, props.type)
                .catch(error => {
                    console.error('Erreur lors de la récupération des documents');
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [props.mandate, props.type, showMessage]);

    /**
     * Pour réinitialiser la page quand on refuse la confirmation
     */
    const handlerQuitPage = async () => {
        props.handlerSetType(undefined, undefined);
    };

    /**
     * Récupération du document correspondant au type de finance
     * Si ça vient après une demande de confirmation, on enregistre la demande
     */
    const handlerGetFile = () => {
        if (props.mandate) {
            setShowFile(true);
            setLoading(true);
            if (props.type === FinanceType.UNPAID || props.type === FinanceType.DEBT) {
                // Enregistrement d'audit indépendant de l'affichage du document : on ne remonte
                // pas l'erreur à l'utilisateur (ex. blocage volontaire lors d'une impersonation).
                askFinanceFile(props.mandate.id, props.type)
                    .then(() => undefined)
                    .catch(error => {
                        console.error("Erreur lors de l'enregistrement de la demande du PDF.", error);
                    });
            }
            // Récupération du document des finances souhaité
            loadFinanceFile(props.mandate.id, props.type)
                .catch(error => {
                    console.error('Erreur lors de la récupération des documents');
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    //Gestion de la vue
    const showContentFile = () => {
        //Skeleton en cours de chargement
        if (loading) {
            return (
                <Grid item xs={12}>
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                </Grid>
            );
        }

        //Si pas en cours de chargement alors on affiche soit le fichier soit un message
        if (file?.data) {
            return (
                <Grid item xs={12} className="financeFileUi__preview">
                    <object
                        aria-label={'pdf'}
                        data-testid={'pdf'}
                        className={'pdf'}
                        style={{ height: 'inherit', width: 'inherit' }}
                        data={file.data}
                    ></object>
                </Grid>
            );
        }

        return (
            <Grid item xs={12} md={6} className="financeFileUi__empty">
                <NoData title={'Information : '} content={'Aucune information à venir'} />
            </Grid>
        );
    };

    //Retourne la vue
    return (
        <Grid container className={'financeFileUi finances-unpaid-content'} spacing={2}>
            {!showFile && (
                <>
                    <Grid item xs={12} md={2}></Grid>
                    <Grid item xs={12} md={8}>
                        <Box className="financeFileUi__confirmation">
                            <Grid container spacing={3}>
                                <Grid item xs={12} className="financeFileUi__confirmationHeader">
                                    <HelpOutlinedIcon />
                                    <Typography variant="h6">Confirmation :</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        Les informations présentes dans cette section sont susceptibles de comporter des
                                        données à caractère personnel protégées. Ces données vous sont mises à
                                        disposition au titre de votre mandat au SDDEA et à sa Régie. Elles doivent être
                                        exploitées exclusivement dans ce cadre. En conséquence, en poursuivant sur cette
                                        page, vous vous engagez à ne pas reproduire et/ou transmettre tout ou partie de
                                        ces informations à tout tiers ainsi qu’à conserver leur caractère strictement
                                        confidentiel. Je souhaite accéder à ces données :
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} className="financeFileUi__actions">
                                    <Button
                                        variant={'outlined'}
                                        className="financeFileUi__action financeFileUi__action--confirm"
                                        onClick={handlerGetFile}
                                    >
                                        <CheckCircleOutlineIcon />
                                        <Typography variant="body2">Oui</Typography>
                                    </Button>
                                    <Button
                                        variant={'outlined'}
                                        className="financeFileUi__action financeFileUi__action--cancel"
                                        onClick={handlerQuitPage}
                                    >
                                        <HighlightOffOutlinedIcon />
                                        <Typography variant="body2">Non</Typography>
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={2}></Grid>
                </>
            )}
            {/* On affiche le PDF dans la page */}
            {showFile && showContentFile()}
        </Grid>
    );
}
